import { plain } from '@app/stationery'
import type { LuachDay, LuachEntry, LuachResult } from './types'

/**
 * What the sheet calls itself — computed here so the printed luach, the screen
 * and the message that carries the same times cannot name the week differently.
 *
 * A weekly Shabbos is titled `שבת קודש` and its parsha; every chag is named for
 * itself. The parsha comes from hebcal already carrying the word פרשת, and it
 * is read across every day of the span because the erev is the day the sheet
 * usually starts on. A Shabbos that is also Yom Tov has no parsha of its own,
 * and falls back to the occasion's own label.
 */
export const sheetTitleHe = (result: LuachResult): string => {
  if (result.occasionKey !== 'shabbos') return result.labelHe
  const parsha = result.days.find((d) => d.parsha)?.parsha
  return parsha ? `שבת קודש ${parsha}` : result.labelHe
}

/** A line's ordering time: a composite orders by its first opinion's time. */
export const firstTime = (e: LuachEntry): string | null =>
  e.parts?.[0]?.time ?? e.time

/**
 * hebcal renders Hebrew with nikud; the printed sheets don't use it. Strip it
 * so the luach reads the way the shul's own does.
 */

/**
 * Which minyan of its tefilla a line is, as the sheet writes it.
 *
 * Open-ended rather than a fixed array: a fourth minyan used to render as
 * `undefined`, which is a shul with one more minyan than the code expected
 * printing a bug onto paper.
 */
const HEBREW_ORDINAL = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'"]
export const minyanOrdinal = (n: number | null | undefined): string =>
  n && n > 0 ? (HEBREW_ORDINAL[n - 1] ?? `${n}`) : ''

/**
 * Wrap a run whose direction is not known in advance, so the text around it
 * cannot reorder it and it cannot reorder the text around it.
 *
 * FIRST STRONG ISOLATE takes its direction from the run's own first strong
 * character, which is the whole point here: the caller does not know whether a
 * room is called בית מדרש or the Simcha Hall, and either is legitimate.
 *
 * The isolate goes INSIDE the brackets rather than around them. A bracket is a
 * neutral character, so with the isolate outside it would take the direction of
 * whatever it sat between; inside, both brackets sit in the surrounding Hebrew
 * and mirror together, which is what keeps them enclosing the name.
 */
const FSI = '⁨'
const PDI = '⁩'
const isolate = (s: string) => `${FSI}${s}${PDI}`

/**
 * A line's Hebrew label with everything that identifies WHICH line it is —
 * which minyan, and which room.
 *
 * One function because there are three renderers: the sheet, an HTML message
 * and an SMS. The sheet lays these out as its own elements for styling, and the
 * two message renderers had been building a label from `labelHe` alone — so the
 * minyan ordinal and the venue silently vanished from every email and text
 * carrying a schedule. A room would have gone the same way.
 *
 * The room is ISOLATED because it is the one part a shul writes in whichever
 * language it says the name, so a Latin name lands inside an otherwise Hebrew
 * line. Left bare, the brackets around it are neutrals between a Hebrew run and
 * a Latin one, and the bidi algorithm resolves them to the paragraph direction —
 * which reorders the room away from the davening it belongs to. The sheet is
 * unaffected because it renders the room as its own isolated element; this is
 * for the two renderers that have only a string, and for SMS there is nothing
 * but the string.
 *
 * The minyan ordinal needs none of this: it is Hebrew, like the label.
 */
export function labelWithPlace(e: LuachEntry): string {
  const ordinal = minyanOrdinal(e.minyanIndex)
  const room = e.room ? ` (${isolate(e.room.name)})` : ''
  return `${plain(e.labelHe)}${ordinal ? ` ${ordinal}` : ''}${room}`
}

/**
 * A line that is a STATEMENT rather than a time — a reminder or announcement.
 *
 * It carries its whole meaning in its text, so it takes the full width and no
 * value beside it. Splitting it into a label and a value prints the same
 * sentence twice, because a note's label and its text are the sentence.
 *
 * One function because there are three renderers: the sheet, an HTML message
 * and an SMS. Each decides the shape of its own row, so each has to be able to
 * ask the question, and a renderer that answers it differently prints a
 * different sheet from the one beside it.
 */
export const isNoteLine = (e: LuachEntry): boolean =>
  !e.time && !e.displayText && !e.parts?.length && Boolean(e.noteText)

/**
 * The days a schedule lists: those with something scheduled.
 *
 * Chol Hamoed is the case that forces this: its shachris is modeled once, on
 * the first Chol Hamoed day, because the workbook prints a single חול המועד
 * block rather than one per day. Listing every date in the span would put
 * headings on the sheet whose only content is the shkia we ourselves insert —
 * days the source sheet never listed. One rule for the sheet, the email and the
 * text, so the three cannot disagree about which days a schedule has; a day
 * whose line did not resolve still has that line, and still keeps its heading.
 */
export const scheduledDays = (days: LuachDay[]): LuachDay[] =>
  days.filter((d) => d.entries.length > 0)

/** "8:18 PM" → minutes past midnight, for ordering against shkia. */
export function toMinutes(t: string | null): number | null {
  if (!t) return null
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(t.trim())
  if (!m) return null
  let h = Number(m[1]) % 12
  if (m[3].toUpperCase() === 'PM') h += 12
  return h * 60 + Number(m[2])
}

/** A line moved here from the previous day — the night that begins this day. */
export const isCarriedNight = (e: LuachEntry) =>
  e.printDayOffset != null && e.dayOffset < e.printDayOffset

/**
 * What tells one printed row from another.
 *
 * The renderer pairs rows across renders by this, so two rows sharing one may
 * be paired the wrong way round — and rows differ in their time and their
 * label, which makes that a wrong time printed under a right name.
 *
 * It is the PLACEMENT that identifies a row, not what the placement says.
 * Composing a key out of properties — the rule, the minyan — only holds while
 * no two placements agree on all of them, and `occasion_line` constrains
 * nothing beyond its primary key: one rule may be placed twice on a day, in two
 * rooms, with no minyan on either. The line's own id is the one field that
 * cannot repeat.
 *
 * The day and the offset are carried defensively. A placement stores one
 * `dayOffset` and is moved rather than copied, so the resolver does not emit
 * one placement twice on a day — but that is the resolver's behavior rather
 * than a constraint, and it costs nothing here to stop depending on it.
 *
 * A function rather than a template at the call site: it states what makes a
 * row unique, and that is only worth anything if it can be asserted.
 */
export const rowKey = (date: string, e: LuachEntry): string =>
  `${date}-${e.lineId}-${e.dayOffset}`

export type ItemEntry = { kind: 'entry'; entry: LuachEntry }
export type Item = ItemEntry | { kind: 'anchor'; time: string }

/**
 * Shkia's name where a day's lines place it — `withSunset`'s anchor carries a
 * time and no label, so a renderer names it from here in either script.
 */
export const SHKIA_LABEL = { en: 'Shkia', he: 'שקיעה' } as const

/**
 * Lay the day out in clock order and slot shkia in where it actually falls. It
 * is the line every other time is measured from, so it belongs between the
 * times before it and the times after it — not appended at the end as one more
 * row.
 *
 * Two things make the ordering more than a sort:
 *
 *  - Untimed lines (a note, or a "sequence" line like "אחר מוסף") have no key
 *    of their own, so they inherit the line above and stay attached to it —
 *    that is what keeps the Shema reminder directly before Shachris.
 *  - The sort is stable on the authored index, so lines sharing a minute
 *    (Kabbalas Shabbos follows mincha at +0) keep the order they were written.
 */
export function withSunset(day: LuachDay): Item[] {
  const entries = day.entries

  // Times of every rule on this day, so a line that follows another can be
  // placed against it.
  const timeOf = new Map<string, number>()
  for (const e of entries) {
    const m = toMinutes(firstTime(e))
    if (m !== null) timeOf.set(e.key, m)
  }

  let carried = -1
  const keyed = entries.map((entry, index) => {
    // A line moved here from the previous day is the NIGHT that begins this day,
    // so it leads the section however late the clock reads.
    if (isCarriedNight(entry)) {
      return { entry, index, key: -10_000 + index }
    }
    const own = toMinutes(firstTime(entry))
    if (own !== null) carried = own
    // An untimed line sorts just AFTER whatever it follows — Shiur Mishnayos is
    // "after Mussaf", so it belongs after Shachris even though the line above it
    // in the source (sof zman shema) resolves earlier.
    const followed = entry.follows ? timeOf.get(entry.follows) : undefined
    const key = own ?? (followed !== undefined ? followed + 0.5 : carried)
    return { entry, index, key }
  })
  keyed.sort((a, b) => a.key - b.key || a.index - b.index)

  const items: Item[] = keyed.map(({ entry }) => ({ kind: 'entry', entry }))

  const shkia = toMinutes(day.anchors.sunset)
  if (!day.anchors.sunset || shkia === null) return items

  /*
   * שקיעה prints because it is the fact the day's times are measured from — so
   * on a day where nothing is measured from it, it says nothing.
   *
   * The case that forces this is the collapsed Chol Hamoed block: one fixed
   * 8:15 shachris standing in for several days, the way the workbook prints it.
   * A sunset there is both irrelevant to the only time shown AND a statement
   * about one particular date that the block does not represent.
   */
  /*
   * A day's OWN lines, and only those. A carried-night line is measured from
   * the PREVIOUS day's sunset, so it cannot be what makes this day's sunset
   * relevant — a motzei Shabbos section holds nothing else, and counting them
   * printed Sunday's shkia under times measured from Saturday's.
   */
  const derivesFromSunset = items.some(
    (i) =>
      i.kind === 'entry' &&
      !isCarriedNight(i.entry) &&
      i.entry.rootZman === 'sunset',
  )
  if (!derivesFromSunset) return items

  /*
   * Place shkia against this day's OWN lines only.
   *
   * The carried-night lines lead the section but are measured from the PREVIOUS
   * day's sunset — a Yom Tov maariv at 7:57 PM sits above a shkia of 7:10 PM
   * because it belongs to the night before. Comparing against them put the
   * anchor at the very top of the day, ahead of the night it does not describe.
   */
  const at = items.findIndex((i) => {
    if (i.kind !== 'entry' || isCarriedNight(i.entry)) return false
    const mins = toMinutes(firstTime(i.entry))
    return mins !== null && mins > shkia
  })
  const anchor: Item = { kind: 'anchor', time: day.anchors.sunset }
  if (at === -1) items.push(anchor)
  else items.splice(at, 0, anchor)
  return items
}

/**
 * What the printed sheet states beside its times: which week it is (a special
 * Shabbos, Shabbos mevorchim), when Rosh Chodesh falls and the molad, and the
 * reason behind a time that replaces the usual one. One reading of the resolved
 * days, shared by the sheet and every page that sets the same schedule, so none
 * of them leaves out what the paper says.
 */
export function sheetAsides(result: LuachResult) {
  const days = scheduledDays(result.days)
  const weekly = result.occasionKey === 'shabbos'
  /*
   * A special-Shabbos event is dated to the Shabbos itself, so reading only the
   * header day (the erev) would never show one — it is collected across all
   * days. שבת מברכים is the same KIND of fact — it says which week this is — so
   * it is announced in the same place, under the title. Its own facts (when
   * Rosh Chodesh falls, when the molad is) are looked up rather than read from
   * across the room, and follow the schedule as an aside.
   */
  const mevorchim = days.map((d) => d.mevorchim).filter((m) => m !== null)
  return {
    week: [
      ...(weekly
        ? [...new Set(days.flatMap((d) => d.specialShabbos ?? []))]
        : []),
      ...mevorchim.map((m) => `שבת מברכים חודש ${m.monthHe}`),
    ].map(plain),
    months: mevorchim.map((m) => ({
      key: m.monthHe,
      roshChodesh: (m.roshChodeshDays ?? []).length
        ? `ראש חודש ${(m.roshChodeshDays ?? []).map((d) => `ביום ${d}`).join(' ו')}`
        : '',
      molad: plain(m.molad),
    })),
    // Times that replace the normal one are asterisked, with the reason printed
    // as a footnote — the same convention the workbook used by hand.
    overrides: [
      ...new Set(
        days.flatMap((d) =>
          d.entries.map((e) => e.overrideNote).filter((n): n is string => !!n),
        ),
      ),
    ],
  }
}
